/**
 * LiveClipShareButton — a viewer-side "Make a 30s clip" button shown on
 * /live/:id that piggy-backs on the existing community-stories pipe to
 * turn the last N seconds of a live broadcast into a shareable mini-VOD.
 *
 * How it works (client-only — no FFmpeg required):
 *   1. Find the on-page <video> element playing the live HLS / WebRTC.
 *   2. Capture its currently playing MediaStream via
 *      `video.captureStream()` and feed it to a `MediaRecorder`.
 *   3. Record for `seconds` (default 30s).
 *   4. Upload the resulting webm to /uploads/video, then POST to
 *      /community/stories so the clip drops onto the stories rail with
 *      a backlink to the live stream.
 *   5. One-tap share via the OS share-sheet (or copy link fallback).
 *
 * Caveats:
 *   • `captureStream()` is supported on Chrome / Edge / Firefox; Safari
 *     16.4+. We feature-detect and disable the button when missing.
 *   • For HLS elements that haven't actually painted yet, the resulting
 *     stream may be black; we gate on `video.readyState >= 2`.
 *   • Audio comes through automatically — `MediaRecorder` records
 *     whatever is on the captured stream.
 */
import React, { useState, useRef, useEffect } from "react";
import { Scissors, Share2, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function LiveClipShareButton({ streamId, streamTitle, creatorName, seconds = 30 }) {
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | recording | uploading | done
  const [clipUrl, setClipUrl] = useState(null);
  const [progress, setProgress] = useState(0);   // 0..1 during recording
  const recRef = useRef(null);

  // If the viewer leaves the page mid-record, abort cleanly so we don't
  // leave a dangling MediaRecorder + mic/camera light.
  useEffect(() => () => {
    try { if (recRef.current?.state === "recording") recRef.current.stop(); } catch (_) {}
  }, []);

  const supported =
    typeof window !== "undefined" &&
    typeof MediaRecorder !== "undefined" &&
    typeof HTMLVideoElement !== "undefined" &&
    typeof HTMLVideoElement.prototype.captureStream === "function";

  const recordAndShare = async () => {
    const v = document.querySelector('[data-testid="watch-page"] video') ||
              document.querySelector('video');
    if (!v) { toast.error("No live video found on this page"); return; }
    if (v.readyState < 2) { toast.error("Stream still loading — try again in a moment"); return; }

    setBusy(true); setPhase("recording");
    let blob;
    try {
      const stream = v.captureStream();
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
        ? "video/webm;codecs=vp9,opus"
        : "video/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      recRef.current = rec;
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
      const stopped = new Promise((resolve) => { rec.onstop = () => resolve(); });
      rec.start();
      // Visible progress strip + countdown toast.
      const startedAt = Date.now();
      setProgress(0);
      const tickToast = setInterval(() => {
        const elapsed = (Date.now() - startedAt) / 1000;
        const left = Math.max(0, seconds - Math.floor(elapsed));
        setProgress(Math.min(1, elapsed / seconds));
        toast(`Clipping… ${left}s`, { id: "clip-toast", duration: 1100 });
      }, 250);
      await new Promise((r) => setTimeout(r, seconds * 1000));
      clearInterval(tickToast);
      setProgress(1);
      try { rec.stop(); } catch (_) {}
      await stopped;
      blob = new Blob(chunks, { type: "video/webm" });
    } catch (e) {
      setBusy(false); setPhase("idle");
      toast.error("Couldn't capture stream — your browser may not support clip-it");
      return;
    }

    setPhase("uploading");
    try {
      const fd = new FormData();
      const file = new File([blob], `clip-${streamId}-${Date.now()}.webm`, { type: "video/webm" });
      fd.append("file", file);
      const up = await api.post("/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const storage = up.data?.storage_path;
      if (!storage) throw new Error("Upload failed");
      const apiBase = api.defaults.baseURL || "";
      const cleanUrl = `${apiBase.replace(/\/+$/, "")}/files/${storage}`;

      // Create a story snippet that links back to the live broadcast.
      const story = await api.post("/community/stories", {
        video_url: cleanUrl,
        title: `📺 Clip from ${creatorName || streamTitle || "live"}`,
        duration_sec: seconds,
        source_kind: "live_clip",
        source_stream_id: streamId,
      });
      const storyId = story.data?.id;

      // Increment the parent stream's clip-count so creators can see how
      // viral their broadcast went.
      api.post(`/live/${streamId}/clip-shared`, { clip_id: storyId }).catch(() => {});

      setClipUrl(`${window.location.origin}/social?story=${storyId || ""}`);
      setPhase("done");
      toast.success("🎉 Clip shared to your stories!");
    } catch (e) {
      toast.error(formatApiError(e));
      setPhase("idle");
    } finally { setBusy(false); }
  };

  const share = async () => {
    if (!clipUrl) return;
    if (navigator.share) {
      try { await navigator.share({ title: streamTitle || "Tap IN clip", url: clipUrl }); return; }
      catch (_) { /* user cancelled */ }
    }
    try { await navigator.clipboard.writeText(clipUrl); toast.success("Clip link copied!"); }
    catch (_) { toast.error("Couldn't copy"); }
  };

  if (!supported) return null;

  return (
    <div data-testid="live-clip-share" className="inline-flex flex-col items-stretch gap-1">
      <div className="inline-flex items-center gap-2">
        <button
          data-testid="live-clip-record-btn"
          onClick={recordAndShare}
          disabled={busy}
          className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 disabled:opacity-60 text-white text-xs font-black px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 shadow-lg"
          title={`Record the last ${seconds} seconds of this stream as a shareable clip`}
        >
          {phase === "recording" ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Clipping…</>
          : phase === "uploading" ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
          : <><Scissors className="w-3.5 h-3.5" /> Clip {seconds}s</>}
        </button>
        {clipUrl && (
          <>
            <button
              data-testid="live-clip-share-btn"
              onClick={share}
              className="bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5"
            >
              <Share2 className="w-3.5 h-3.5" /> Share clip
            </button>
            <a
              data-testid="live-clip-view-btn"
              href={clipUrl}
              target="_blank"
              rel="noreferrer"
              className="bg-tv-accent/20 hover:bg-tv-accent/30 text-tv-accent text-xs font-bold px-3 py-1.5 rounded-full"
            >
              View
            </a>
          </>
        )}
      </div>
      {phase === "recording" && (
        <div
          data-testid="live-clip-progress"
          className="h-1 bg-white/10 rounded-full overflow-hidden"
          aria-label={`Recording progress ${Math.round(progress * 100)}%`}
        >
          <div
            className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-200"
            style={{ width: `${Math.round(progress * 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}
